import { type AppearanceSettings, DEFAULT_APPEARANCE_SETTINGS, resolveAppearance } from 'ProdantixSdk/chat/appearance';
import type * as Articles from 'ProdantixSdk/chat/articles';
import type { HelpHost } from 'ProdantixSdk/chat/articles';
import type { ArticleCollection, ArticleHit, ArticleRef, ChatIdentity, ChatThread, ChatTurn, ChatUpload, PageContext } from 'ProdantixSdk/chat/client';
import type * as Files from 'ProdantixSdk/chat/files';
import { isOpenAt } from 'ProdantixSdk/chat/hours';
import { buildLauncherContent, buildTeamFaces, initialsOf, type LauncherFace, launcherCss } from 'ProdantixSdk/chat/launcher';
import type * as Tickets from 'ProdantixSdk/chat/tickets';
import type { MessengerConfig, MessengerHelp, StarterPrompt } from 'ProdantixSdk/messenger/config';

/// Copy is passed in, not hardcoded, so a host can localise the widget. The
/// defaults are English and deliberately terse.
export interface ChatStrings {
  launcherLabel: string;
  title: string;
  inputPlaceholder: string;
  sendLabel: string;
  awayBody: string;
  closeLabel: string;
  agentLabel: string;
  aiLabel: string;
  unreadLabel: (count: number) => string;
  /// Takes the turn's ISO timestamp so a host can localise it. The default is
  /// deliberately 24-hour and locale-free: the widget renders inside somebody
  /// else's page and must not pull a formatting library to do it.
  timeLabel: (createdAt: string) => string;
  /// The first-run panel's heading when a project has configured no greeting.
  /// The greeting itself is per-project and per-locale and arrives on the
  /// appearance, so this is only the fallback.
  emptyTitle: string;
  /// The line under the greeting. Copy about US rather than about the project,
  /// which is why it is a string here and not a column.
  emptyBody: string;
  /// The header's status line, beside the live dot. Shorter than `emptyBody`
  /// and doing a different job: this one sits under the title next to a dot
  /// that says somebody is there.
  replyTime: string;
  /// On a turn whose send did not land. Says both that it failed and what to do
  /// about it, because the bubble it sits in is the only notice the visitor
  /// gets.
  retryLabel: string;
  attachLabel: string;
  removeFileLabel: string;
  fileRefusal: (size: string, limit: string) => string;
}

const DEFAULT_STRINGS: ChatStrings = {
  agentLabel: 'Agent',
  aiLabel: 'AI',
  attachLabel: 'Attach a file',
  awayBody: 'We are away right now, but we will reply as soon as we are back.',
  closeLabel: 'Close chat',
  emptyBody: 'We usually reply within a few minutes.',
  emptyTitle: 'Ask us anything.',
  fileRefusal: (size, limit) => `That file is ${size}. Send a PNG, JPEG, WebP, GIF or PDF up to ${limit}.`,
  inputPlaceholder: 'Type a message',
  launcherLabel: 'Chat with us',
  removeFileLabel: 'Remove file',
  replyTime: 'Usually replying in a few minutes',
  retryLabel: 'Not delivered. Retry',
  sendLabel: 'Send',
  timeLabel: createdAt => {
    const at = new Date(createdAt);
    return Number.isNaN(at.getTime()) ? '' : `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
  },
  title: 'Support',
  unreadLabel: count => `${count} unread`,
};

/// A closed shadow root: the panel's markup is invisible to the host page's
/// CSS and querying, and the host's styles cannot leak in and collapse the
/// widget (the classic support-widget failure). All styles are scoped here.
/// The launcher geometry, accent, first-run copy and hours a project
/// configured. A subset of `MessengerConfig`.
///
/// `greeting` and `starterPrompts` were deliberately excluded here until now, on
/// the reasoning that the panel did not need them. It did: the panel opened onto
/// an empty log, the greeting had been stored, served and parsed since 0035 with
/// nothing rendering it, and the console preview drew a first-run view the
/// shipped widget had no concept of.
export type MessengerAppearance = Pick<MessengerConfig, 'accentColor' | 'attachments' | 'availability' | 'awayMessage' | 'greeting' | 'launcherOffsetX' | 'launcherOffsetY' | 'launcherPosition' | 'help' | 'poweredBy' | 'starterPrompts' | 'team' | 'tickets'> & AppearanceSettings;

/// What the panel paints before any configuration has been read, and what every
/// pre-existing caller gets. NOT the old `#16a34a`, and no longer the `#1d4ed8`
/// that replaced it: both were placeholders in no palette we own. This is
/// `--bomdisoft-primary` from `packages/theme/typescript/src/tokens.css`,
/// resolved to hex because the widget ships zero dependencies and cannot read
/// the sheet. The LIGHT value of the pair: the panel paints one scheme and sets
/// `color: #fff` on this colour three times below, which `#5958fc` carries at
/// 4.97:1 where the dark `#7881ff` reaches only 3.29:1.
export const DEFAULT_APPEARANCE: MessengerAppearance = {
  accentColor: '#5958fc',
  availability: { timezone: 'UTC', windows: [] },
  awayMessage: '',
  greeting: '',
  launcherOffsetX: 20,
  launcherOffsetY: 20,
  launcherPosition: 'bottom_right',
  starterPrompts: [],
  team: [],
  ...DEFAULT_APPEARANCE_SETTINGS,
};

export function poweredMark(words: string, logoUrl: string): HTMLAnchorElement {
  const mark = document.createElement('a');
  mark.className = 'powered';
  mark.href = 'https://prodantix.com';
  mark.target = '_blank';
  mark.rel = 'noopener';
  const logo = document.createElement('img');
  logo.src = logoUrl;
  logo.alt = '';
  logo.width = 12;
  logo.height = 12;
  mark.append(logo, words);
  return mark;
}

/// The stylesheet as a pure function of configuration, so the accent and the
/// corner are testable without mounting anything into a closed shadow root.
/// Per-axis fragments, appended to one base sheet. Only the selected form's
/// rules are emitted, which is what keeps twenty-three shipped forms inside the
/// 15 kB budget. The approved record, `docs/design/messenger-record.html`, is
/// the authority for every value.
const SKIN_CSS: Record<AppearanceSettings['skin'], string> = {
  /* The send glyph takes `--ink`, not `--a`, which is the one place this file
     departs from the record. The accent is a tenant's own hex and the widget
     follows the visitor's `prefers-color-scheme`, so an accent used as TEXT has
     to clear 4.5:1 against both `--paper` values, and no flat hex does: the
     brand `#5958fc` measures 4.97 on the light paper and 3.26 on the dark one.
     Everywhere else the accent is a background carrying `#fff`, which is the
     single axis `accent_refusal` can enforce.

     Quiet owns its header, its corner and the send inside the field. The field
     itself is the composer axis's, the same in both skins (ruling 2 of
     2026-09-13): a skin that put a border on it was louder, not quieter. */
  quiet:
    '.composer{position:relative;padding:8px 10px 10px}.composer input{padding-right:36px}.composer button{position:absolute;top:50%;right:14px;width:28px;min-width:28px;height:28px;padding:0;background:transparent;color:var(--ink);border-radius:7px;transform:translateY(-50%)}.composer button .sendglyph{display:block;width:16px;height:16px}.composer button .sendtext{display:none}.panel{border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,.12)}.header{background:var(--paper);color:var(--ink);height:44px;padding:0 8px 0 16px;border-bottom:1px solid var(--hairline)}.header h2{font-size:11px;letter-spacing:.11em;text-transform:uppercase;color:var(--ink-3)}.header .faces,.header .status{display:none}.header .close{top:6px}.close{color:var(--ink-2)}',
  standard: '.panel{border-radius:16px;box-shadow:0 16px 40px rgba(0,0,0,.2)}.header{background:var(--a);color:#fff;flex-direction:column;align-items:flex-start;gap:14px;padding:18px 14px 20px 20px}.header h2{font-size:19px;font-weight:640}',
};

/// The composer's corner, its own axis since 2026-09-13. Round is the pill
/// field with the disc send and the glyph; square is the 8px field with the
/// labelled send. Neither draws a border: the field is `--fill` on `--paper` in
/// both, and the skin never touches it.
const COMPOSER_CSS: Record<AppearanceSettings['composerShape'], string> = {
  round: '.composer{padding:10px 10px 12px;gap:6px}.composer input{height:36px;border-radius:18px;padding:0 14px}.composer button{width:36px;min-width:36px;height:36px;padding:0;border-radius:18px}.composer button .sendglyph{display:block}.composer button .sendtext{display:none}',
  square: '.composer input{height:36px;border-radius:8px;padding:0 12px}',
};

/// Shape decides the corners and fill decides whether the turn is an object on
/// the surface or an outline around the words. Two axes since 2026-09-13; the
/// hairline style that drew no bubble is gone.
const BUBBLE_SHAPE_CSS: Record<AppearanceSettings['bubbleShape'], string> = {
  rounded: '.turn{border-radius:18px;padding:10px 15px}',
  tailed: '.turn.agent,.turn.ai{border-radius:12px 12px 12px 4px}.turn.end_user{border-radius:12px 12px 4px 12px}',
};

const BUBBLE_FILL_CSS: Record<AppearanceSettings['bubbleFill'], string> = {
  filled: '',
  outlined: '.turn{background:none;border:1px solid var(--hairline)}.turn.end_user{background:none;border-color:var(--a);color:var(--ink)}',
};

const NAME_CSS: Record<AppearanceSettings['namePlacement'], string> = {
  above: '',
  hidden: '.turn.agent .author,.turn.ai .author:not(.ai){display:none}',
  inline: '.body .author.below{display:block;margin:4px 0 0;font-size:11.5px;font-weight:500;color:var(--ink-2);text-transform:none;letter-spacing:0;opacity:1}',
};

/* `.turn .body` used to carry the fill for EVERY turn. The visitor's turn keeps
   its own accent background, so it drew a filled bubble inside a filled bubble:
   two bubbles around one message, which is what the launcher's own screenshot
   showed. Only the agent and ai turns become avatar rows, so only their bodies
   are the bubble. */
const AVATAR_CSS = '.turn.agent,.turn.ai{display:flex;gap:10px;max-width:100%;background:none;padding:0}.turn.agent .body,.turn.ai .body{background:var(--fill);padding:9px 13px;border-radius:inherit}.author{text-transform:none;letter-spacing:0;font-size:12.5px;font-weight:600;opacity:1}';

const DIVIDER_CSS = '.divider{display:flex;align-items:center;gap:10px;margin:0;font-size:11px;opacity:.6}.divider:before,.divider:after{content:"";flex:1;height:1px;background:var(--hairline)}';

const STAMP_CSS: Record<AppearanceSettings['stampPlacement'], string> = {
  beside_name: '.stamp{font-size:11px;opacity:.7;margin-left:6px}',
  none: '',
  under_turn: '.stamp{display:block;font-size:11px;opacity:.7;margin-top:4px}.turn.end_user .stamp{text-align:right}',
};

const LATENCY_CSS = '.lat{font-size:11px;opacity:.7;margin-left:6px}';

/// What the panel asks of whatever feeds it: a thread, a way to send, and a way
/// to complete an avatar path. `ChatClient` satisfies it; so does the console's
/// preview, which draws the REAL panel over sample turns rather than restating
/// its markup in React. Two drawings of one widget is how the header came to
/// carry the team's faces in the console and not in the widget.
export interface ChatSource {
  avatarUrl(url: string): string;
  load(): Promise<ChatThread | null>;
  send(body: string, pageContext?: PageContext, attachmentIds?: string[]): Promise<ChatTurn | null>;
  upload?(file: File): Promise<ChatUpload | null>;
  file?(path: string): Promise<Blob | null>;
  articleFrame?(article: ArticleRef): Promise<string | null>;
  articles?(query: string): Promise<ArticleHit[]>;
  popularArticles?(): Promise<ArticleHit[]>;
  collections?(): Promise<ArticleCollection[]>;
  collectionArticles?(collection: string): Promise<ArticleHit[]>;
  helpCentre?<T>(suffix: string, params: Record<string, string>, body?: Record<string, string>): Promise<T | null>;
  edge?<T>(path: string, body?: unknown, token?: string): Promise<T | null>;
  readonly visitor?: (ChatIdentity & { distinctId: string }) | null;
}

/// Which token set the widget paints with.
///
/// `system` follows the visitor's `prefers-color-scheme` and is what every
/// embedder gets. The console's preview forces one, because a reader reviewing
/// the dark scheme cannot be asked to change their OS setting to see it.
export type ChatScheme = 'dark' | 'light' | 'system';

/// How the panel is attached, as against what it draws.
/// The help centre's placements and reader, which every visitor would otherwise
/// download whether or not the project searches articles. They arrive only when
/// the project's settings carry a help block.
export type HelpKit = typeof Articles;

/// The paperclip, the chosen file and a turn's files, fetched only by a project
/// that takes files or a thread that holds one.
export type FilesKit = typeof Files;

/// The Tickets tab, its list, form and a ticket's thread, fetched only by a
/// project that takes tickets.
export type TicketsKit = typeof Tickets;

export interface ChatPanelOptions {
  help?: () => Promise<HelpKit | null>;
  files?: () => Promise<FilesKit | null>;
  tickets?: () => Promise<TicketsKit | null>;
  scheme?: ChatScheme;
  /// `closed` everywhere a visitor sees it. `open` only where the drawing has
  /// to be readable, which is the console's preview and its journeys.
  shadowMode?: 'closed' | 'open';
}

const DARK_TOKENS = `--paper: oklch(24.6% 0.015 257); --ink: #fff; --ink-3: rgba(255,255,255,.55);
    --fill: rgba(255,255,255,.06); --hairline: rgba(255,255,255,.13); --ink-2: rgba(255,255,255,.70);
    --online: oklch(72% 0.13 163.23);`;

/// The dark tokens, behind the visitor's own preference or forced on.
///
/// A forced scheme cannot be done from outside: these live on `:host` inside
/// the widget's shadow root, and a media query there answers the OS and
/// nothing else. That is why the console's preview could not mount the real
/// widget to show its dark half, and why it drew a second one instead.
function darkTokens(scheme: ChatScheme): string {
  if (scheme === 'light') return '';
  if (scheme === 'dark') return `:host { ${DARK_TOKENS} }`;
  return `@media (prefers-color-scheme: dark) {\n  :host {\n    ${DARK_TOKENS}\n  }\n}`;
}

const URL_RUN = /https?:\/\/[^\s<>"]+/g;
const TRAILING = /[.,;:!?)]$/;

const trimmed = (run: string): string => {
  let url = run;
  while (TRAILING.test(url)) {
    const opens = url.split('(').length - 1;
    const closes = url.split(')').length - 1;
    if (url.endsWith(')') && opens >= closes) break;
    url = url.slice(0, -1);
  }
  return url;
};

export function linkify(parent: HTMLElement, words: string): void {
  let at = 0;
  for (const match of words.matchAll(URL_RUN)) {
    const start = match.index ?? 0;
    const url = trimmed(match[0]);
    if (start > at) parent.append(words.slice(at, start));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.textContent = url;
    parent.append(anchor);
    at = start + url.length;
  }
  if (at < words.length) parent.append(words.slice(at));
}

const PROMPT_TAGS = ['P', 'BR', 'STRONG', 'EM', 'A', 'UL', 'OL', 'LI'];

const isStarterPrompt = (entry: unknown): entry is StarterPrompt => typeof entry === 'object' && entry !== null && 'html' in entry && typeof entry.html === 'string' && 'text' in entry && typeof entry.text === 'string';

const isDrawableAttribute = (tag: string, attribute: Attr): boolean => {
  if (attribute.name === 'class' || attribute.name === 'data-block-type') return true;
  if (tag !== 'A') return false;
  if (attribute.name === 'target' || attribute.name === 'rel') return true;
  return attribute.name === 'href' && /^https?:\/\//i.test(attribute.value.trim());
};

const isDrawable = (content: DocumentFragment): boolean => [...content.querySelectorAll('*')].every(element => PROMPT_TAGS.includes(element.tagName) && [...element.attributes].every(attribute => isDrawableAttribute(element.tagName, attribute)));

export function panelStyles(appearance: MessengerAppearance, scheme: ChatScheme = 'system'): string {
  const settings = resolveAppearance(appearance);
  const side = appearance.launcherPosition === 'bottom_left' ? 'left' : 'right';
  const accent = appearance.accentColor;
  const base = `:host{all:initial;z-index:2147483000;--a:${accent};--paper:#fff;--ink:#0f172a;--ink-3:rgba(0,0,0,.55);--fill:#f1f5f9;--hairline:#e2e8f0;--ink-2:rgba(0,0,0,.72);--online:oklch(53.8% 0.1274 163.23);color-scheme:${scheme === 'system' ? 'light dark' : scheme}}${darkTokens(scheme)}*{box-sizing:border-box;font-family:system-ui,sans-serif}.launcher{z-index:2147483000;position:fixed;${side}:${appearance.launcherOffsetX}px;bottom:${appearance.launcherOffsetY}px;width:56px;height:56px;border-radius:28px;border:none;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;background:${accent};color:#fff;font-size:24px;box-shadow:0 4px 12px rgba(0,0,0,.2)}.launcher:focus-visible{outline:3px solid #0b0e14;outline-offset:2px}.launcher{transition:background-color 120ms ease}.launcher:hover{background:color-mix(in srgb,#000 12%,${accent})}.launcher:active{background:color-mix(in srgb,#000 20%,${accent})}@media (prefers-reduced-motion:reduce){.launcher{transition:none}}.badge{position:absolute;top:-4px;right:-4px;min-width:20px;height:20px;border-radius:10px;background:#dc2626;color:#fff;font-size:12px;display:flex;align-items:center;justify-content:center;padding:0 4px}.badge[hidden]{display:none}.panel{z-index:2147483001;position:fixed;${side}:${appearance.launcherOffsetX}px;bottom:${appearance.launcherOffsetY + 68}px;width:360px;max-width:calc(100vw - 40px);height:520px;max-height:calc(100vh - 120px);background:var(--paper);color:var(--ink);border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,.24);display:flex;flex-direction:column;overflow:hidden}.header{position:relative;padding:14px 16px;background:#0b0e14;color:#fff;display:flex;align-items:center;justify-content:space-between}.header h2{margin:0;font-size:15px}.header .faces{display:flex}.header .av{width:36px;height:36px;box-shadow:0 0 0 2px var(--a)}.header .av + .av{margin-left:-12px}.headmain{display:flex;flex-direction:column}.header .status{display:flex;align-items:center;gap:6px;margin:5px 0 0;font-size:12.5px;color:rgb(255 255 255 / 80%)}.header .status .live{flex:none;width:10px;height:10px;border-radius:50%;background:var(--online);box-shadow:0 0 0 2px var(--paper)}.panel.closed .status{display:none}.header .close{position:absolute;top:8px;right:8px}.close{display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;padding:0;background:none;border:none;border-radius:8px;color:inherit;cursor:pointer;transition:background-color 120ms ease}.close svg{display:block;width:22px;height:22px}.close:hover{background:color-mix(in srgb,currentColor 14%,transparent)}.close:focus-visible{outline:2px solid currentColor;outline-offset:1px}.log{flex:1;overflow-y:auto;padding:12px;display:flex;flex-direction:column;gap:8px}.turn{max-width:80%;padding:8px 12px;border-radius:12px;font-size:14px}.turn.end_user{align-self:flex-end;background:${accent};color:#fff}.turn.agent,.turn.ai{align-self:flex-start;background:var(--fill);color:var(--ink)}.turn.system{align-self:center;font-size:12px;color:var(--ink-3)}.turn.ai a{color:${accent};text-decoration:underline;overflow-wrap:anywhere}.author{font-size:11px;opacity:.7;margin-bottom:2px}.turn[data-pending]{opacity:.6}.turn[data-failed]{opacity:1;box-shadow:inset 0 0 0 1px #dc2626}.retry{display:block;margin:5px 0 0;padding:0;background:none;border:0;color:inherit;font:inherit;font-size:11.5px;text-decoration:underline;cursor:pointer;opacity:.85}.away{display:flex;gap:10px;align-items:flex-start;margin:12px 12px 0;padding:10px 14px;font-size:13px;line-height:1.43;color:${accent};background:color-mix(in srgb,${accent}12%,transparent);border-radius:4px}.awayglyph{flex:none;width:16px;height:16px;margin-top:1px;border:1.5px solid currentColor;border-radius:50%}.empty{display:flex;flex-direction:column;gap:8px;padding:20px 16px}.empty h3{margin:0;font-size:18px;font-weight:640;letter-spacing:-.015em}.empty > p{margin:0 0 8px;font-size:13.5px;color:var(--ink-2)}.prompt{position:relative;display:flex;align-items:center;min-height:44px;padding:10px 14px;text-align:left;font-family:inherit;font-size:13.5px;font-weight:500;color:var(--ink);background:none;border:1px solid var(--hairline);border-radius:8px;cursor:pointer}.promptwords{min-width:0;overflow-wrap:anywhere}.promptwords > :last-child{margin-bottom:0}.prompt p{margin:0 0 16px;line-height:1.6}.prompt ul,.prompt ol{margin:0 0 16px;padding-left:32px}.prompt li{margin:8px 0;line-height:1.6}.prompt a{position:relative;z-index:1;color:${accent};text-decoration:underline;overflow-wrap:anywhere}.promptpress{position:absolute;inset:0;padding:0;background:none;border:0;border-radius:inherit;cursor:pointer}.promptpress:focus-visible{outline:2px solid ${accent};outline-offset:2px}.av{width:30px;height:30px;border-radius:50%;flex:none;display:inline-flex;align-items:center;justify-content:center;font-size:11.5px;font-weight:650;color:#fff;background:oklch(52% 0.17 22)}.av.bot{border-radius:9px;background:var(--fill);color:var(--ink)}.av img{width:100%;height:100%;display:block;object-fit:cover;border-radius:inherit}.composer{display:flex;gap:6px;padding:10px;border-top:1px solid var(--hairline)}.composer input{flex:1;min-width:0;height:36px;padding:0 12px;border:0;border-radius:8px;font-size:14px;background:var(--fill);color:var(--ink)}.composer input:focus-visible{outline:2px solid ${accent}}.composer button{display:inline-flex;align-items:center;justify-content:center;gap:0;min-width:56px;height:36px;padding:0 14px;background:${accent};color:#fff;border:none;border-radius:8px;cursor:pointer;font-family:inherit;font-size:13.5px;font-weight:550}.composer button:hover{background:color-mix(in srgb,#000 12%,${accent})}.composer button:focus-visible{outline:2px solid var(--ink);outline-offset:2px}.composer button .sendglyph{display:none;width:17px;height:17px}.composer button:focus-visible{outline:2px solid #0b0e14}.powered{display:flex;align-items:center;justify-content:center;gap:4px;height:24px;font-size:11px;font-weight:500;color:var(--ink-3);text-decoration:none}.powered:hover{color:var(--ink-2)}.powered:focus-visible{outline:2px solid var(--ink);outline-offset:-2px}.powered img{width:12px;height:12px}@media (prefers-reduced-motion:no-preference){.panel{animation:rise 180ms ease-out}@keyframes rise{from{transform:translateY(8px);opacity:0}to{transform:none;opacity:1}}}.scrim,.grip{display:none}@media (max-width:770px){.panel{inset:auto 0 0;width:100%;max-width:none;height:86dvh;max-height:86dvh}:host .panel{border-bottom-left-radius:0;border-bottom-right-radius:0}.scrim{display:block;position:fixed;inset:0;z-index:2147483000;background:rgb(15 23 42/.24)}.grip{display:block;position:absolute;top:7px;left:calc(50% - 17px);width:34px;height:4px;border-radius:2px;background:currentColor;opacity:.45}.panel:not([data-dragging]){transition:translate .2s}.composer{padding-bottom:max(12px,env(safe-area-inset-bottom))}}`;
  return `${base}${COMPOSER_CSS[settings.composerShape]}${SKIN_CSS[settings.skin]}${launcherCss(settings.launcherShape, settings.launcherFill, settings.launcherContent, settings.launcherSize)}${BUBBLE_SHAPE_CSS[settings.bubbleShape]}${BUBBLE_FILL_CSS[settings.bubbleFill]}${NAME_CSS[settings.namePlacement]}${settings.nameAvatar ? AVATAR_CSS : ''}${settings.dayDividers ? DIVIDER_CSS : ''}${STAMP_CSS[settings.stampPlacement]}${settings.stampContent === 'time_and_latency' ? LATENCY_CSS : ''}`;
}

const AUTHOR_KINDS: ChatTurn['authorKind'][] = ['end_user', 'agent', 'ai'];

/// Read at the moment of sending rather than at open: a visitor can navigate
/// with the panel up, and the agent needs the page the words were written on.
/// The pathname alone, never the query or the fragment: a reset link, a magic
/// link or a checkout return carries its token there, and an agent's inbox is
/// no place for it.
function pageContextNow(): PageContext | undefined {
  const location = (globalThis as { location?: Location }).location;
  if (!location) return undefined;
  return { host: location.host, path: location.pathname, title: document.title };
}

/// The close control's glyph: the fleet's caret (`ExpandMoreRounded`), because
/// the panel is a sheet the visitor puts down, not a thing they destroy (user
/// ruling, 2026-09-20). Inline for the same reason as the launcher's glyph: no
/// icon package reaches inside a closed shadow root.
const CLOSE_GLYPH = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M15.88 9.29 12 13.17 8.12 9.29a.996.996 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l4.59 4.59c.39.39 1.02.39 1.41 0l4.59-4.59c.39-.39.39-1.02 0-1.41-.39-.38-1.03-.39-1.42 0"/></svg>';

/// The launcher + panel, rendered into a closed shadow root. Accessibility is
/// not optional (see `AGENTS.md`): the launcher is a button
/// with an accessible name and an unread count in its label (not colour), the
/// panel is a `role="dialog"` with focus trapped while open and restored on
/// close, and the turn list is an `aria-live="polite"` log.
export class ChatPanel {
  private readonly root: ShadowRoot;
  private readonly strings: ChatStrings;
  private settings: AppearanceSettings;
  private appearance: MessengerAppearance;
  private readonly launcher: HTMLButtonElement;
  private readonly badge: HTMLElement;
  private panel: HTMLElement | null = null;
  private scrim: HTMLElement | null = null;
  private lastFocused: Element | null = null;
  private unread = 0;
  /// Numbers the optimistic bubbles so two in flight at once cannot share an
  /// id. Local only: each one is replaced by the server's id when it lands.
  private pendingTurns = 0;
  /// Whether the project is inside its hours right now, so a panel built
  /// later is built in the state the launcher is already showing.
  private openNow: boolean;
  private readonly styles: (css: string) => void;
  private scheme: ChatScheme | undefined;
  private picked: File | null = null;
  private objectUrls: string[] = [];
  private readonly helpLoader: (() => Promise<HelpKit | null>) | undefined;
  private kit: HelpKit | null = null;
  private kitLoad: Promise<HelpKit | null> | null = null;
  private readonly filesLoader: (() => Promise<FilesKit | null>) | undefined;
  private filesKit: FilesKit | null = null;
  private filesLoad: Promise<FilesKit | null> | null = null;
  private readonly ticketsLoader: (() => Promise<TicketsKit | null>) | undefined;
  private ticketsLoad: Promise<TicketsKit | null> | undefined;

  constructor(
    private readonly host: HTMLElement,
    private readonly client: ChatSource,
    strings: Partial<ChatStrings> = {},
    appearance: Partial<MessengerAppearance> = {},
    options: ChatPanelOptions = {},
  ) {
    this.strings = { ...DEFAULT_STRINGS, ...strings };
    this.appearance = { ...DEFAULT_APPEARANCE, ...appearance };
    this.settings = resolveAppearance({ ...DEFAULT_APPEARANCE, ...appearance });
    /* Closed for an embedder, which is the point: the host page's script has no
       way in. `mode` decides visibility to JS and nothing about style
       isolation, so the console's preview opens it to let the console and its
       journeys read what the widget actually drew. */
    this.root = host.attachShadow({ mode: options.shadowMode ?? 'closed' });
    /* Seeded from the hours so a panel built before any caller reports presence
       behaves as it always did, and overridable so the one loop that polls the
       hours is the only thing that decides. The away message used to compute
       this a second time from the same availability, which agreed with the
       launcher's dot only because mountChat handed both the same function's
       answer. */
    this.openNow = isOpenAt(this.appearance.availability, new Date());
    this.scheme = options.scheme;
    this.helpLoader = options.help;
    this.filesLoader = options.files;
    this.ticketsLoader = options.tickets;
    this.styles = styleShadowRoot(this.root);
    this.paint();

    this.launcher = document.createElement('button');
    this.launcher.className = 'launcher';
    this.launcher.type = 'button';
    this.launcher.setAttribute('aria-haspopup', 'dialog');
    this.launcher.addEventListener('click', () => this.toggle());

    this.badge = document.createElement('span');
    this.badge.className = 'badge';
    this.badge.hidden = true;

    this.drawLauncher();
    this.root.append(this.launcher);
  }

  /// The launcher's own children, which every appearance axis can change.
  /// Rebuilt rather than patched, and the badge is re-appended rather than
  /// recreated so an unread count survives a restyle.
  private drawLauncher(): void {
    this.launcher.setAttribute('aria-label', this.strings.launcherLabel);
    this.launcher.replaceChildren();
    this.launcher.append(buildLauncherContent(this.settings.launcherContent, this.strings, this.appearance.team, url => this.client.avatarUrl(url)));
    if (this.settings.launcherShape === 'pill') {
      const label = document.createElement('span');
      label.className = 'label';
      label.textContent = this.strings.launcherLabel;
      this.launcher.append(label);
    }
    this.launcher.append(this.badge);
    this.launcher.classList.toggle('closed', !this.openNow);
  }

  /// Repaint against a changed appearance, in place.
  ///
  /// For a surface that edits the appearance live, which is the console's
  /// Configure tab: it draws THIS widget rather than a second one in React, and
  /// a reader typing in the accent field expects the drawing to follow. Tearing
  /// the panel down and rebuilding it per keystroke would lose whatever the
  /// reader had open, so the style is reassigned and the panel redrawn only
  /// when one is up.
  update(appearance: Partial<MessengerAppearance>, scheme?: ChatScheme): void {
    this.appearance = { ...DEFAULT_APPEARANCE, ...appearance };
    this.settings = resolveAppearance(this.appearance);
    if (scheme !== undefined) this.scheme = scheme;
    this.paint();
    this.drawLauncher();
    if (this.panel) {
      this.close();
      void this.open();
    }
  }

  private paint(): void {
    this.styles(`${panelStyles(this.appearance, this.scheme)}${this.kit && this.appearance.help ? this.kit.HELP_CSS : ''}${this.filesKit ? this.filesKit.filesCss(this.appearance.attachments?.enabled === true, this.settings.composerShape) : ''}`);
  }

  /// Asked once per panel, and only for a project whose settings carry a help
  /// block, so a visitor on any other site downloads none of it.
  private loadKit(): Promise<HelpKit | null> {
    if (!this.appearance.help || !this.helpLoader) return Promise.resolve(null);
    this.kitLoad ??= this.helpLoader().then(kit => {
      this.kit = kit;
      this.paint();
      return kit;
    });
    return this.kitLoad;
  }

  private loadFiles(): Promise<FilesKit | null> {
    if (!this.filesLoader) return Promise.resolve(null);
    this.filesLoad ??= this.filesLoader().then(kit => {
      this.filesKit = kit;
      this.paint();
      return kit;
    });
    return this.filesLoad;
  }

  private toggle(): void {
    if (this.panel) {
      this.close();
    } else {
      void this.open();
    }
  }

  /// The dot on the launcher says whether someone is there, which is the
  /// project's Hours and nothing about how the launcher is drawn (ruling 3 of
  /// 2026-09-13). Told from outside so the clock that re-asks lives with the
  /// other timers in `mountChat`.
  setOpenNow(open: boolean): void {
    this.openNow = open;
    this.launcher.classList.toggle('closed', !open);
    /* The panel carries it too, and only exists while it is open, so the class
       is re-applied when one is built. The header claims somebody is there;
       outside the hours that claim is drawn by nothing. */
    this.panel?.classList.toggle('closed', !open);
  }

  async open(): Promise<void> {
    if (this.panel) return;
    this.lastFocused = this.host.ownerDocument.activeElement;
    this.unread = 0;
    this.renderBadge();

    const panel = document.createElement('section');
    panel.className = this.openNow ? 'panel' : 'panel closed';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', this.strings.title);

    /* The header the Configure tab draws: the team's faces, the title, and a
       status line carrying the live dot. It used to be a title and a close
       button and nothing else, so the preview a project signed off on and the
       widget its visitors saw had different headers. */
    const header = document.createElement('div');
    header.className = 'header';
    const grip = document.createElement('span');
    grip.className = 'grip';
    grip.setAttribute('aria-hidden', 'true');
    header.append(grip);
    const team = this.appearance.team ?? [];
    if (team.length > 0) header.append(buildTeamFaces(team, this.strings, url => this.client.avatarUrl(url)));
    const headMain = document.createElement('div');
    headMain.className = 'headmain';
    const heading = document.createElement('h2');
    heading.textContent = this.strings.title;
    const status = document.createElement('p');
    status.className = 'status';
    const statusDot = document.createElement('span');
    statusDot.className = 'live';
    statusDot.setAttribute('aria-hidden', 'true');
    status.append(statusDot, this.strings.replyTime);
    headMain.append(heading, status);
    const close = document.createElement('button');
    close.className = 'close';
    close.type = 'button';
    close.setAttribute('aria-label', this.strings.closeLabel);
    close.innerHTML = CLOSE_GLYPH;
    close.addEventListener('click', () => this.close());
    header.append(headMain, close);

    const log = document.createElement('div');
    log.className = 'log';
    log.setAttribute('role', 'log');
    log.setAttribute('aria-live', 'polite');
    log.setAttribute('aria-label', this.strings.title);

    const composer = document.createElement('form');
    composer.className = 'composer';
    const input = document.createElement('input');
    input.type = 'text';
    input.setAttribute('aria-label', this.strings.inputPlaceholder);
    input.placeholder = this.strings.inputPlaceholder;
    /* Both a glyph and the word, with the composer deciding which is shown:
       the accessible name is the word in every form, while the round composer's
       46px disc and quiet's 34px inline square have no room to print it. */
    const send = document.createElement('button');
    send.type = 'submit';
    send.setAttribute('aria-label', this.strings.sendLabel);
    send.innerHTML = '<svg class="sendglyph" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 12 20 4.5 15 20l-3.6-5.4L4.5 12Z"/></svg>';
    const sendText = document.createElement('span');
    sendText.className = 'sendtext';
    sendText.textContent = this.strings.sendLabel;
    send.append(sendText);
    composer.append(input, send);
    composer.addEventListener('submit', event => {
      event.preventDefault();
      void this.submit(input, log);
    });

    panel.append(header, log, composer);
    if (this.appearance.poweredBy) panel.append(poweredMark(this.appearance.poweredBy, this.client.avatarUrl('/messenger-mark.png')));
    panel.addEventListener('keydown', event => this.onKeydown(event, panel, close));
    const scrim = document.createElement('div');
    scrim.className = 'scrim';
    scrim.addEventListener('click', () => this.close());
    this.root.append(scrim, panel);
    this.panel = panel;
    this.scrim = scrim;
    dragToClose(panel, () => this.close());

    input.focus();
    const taking = this.appearance.attachments;
    const [thread, , , tickets] = await Promise.all([this.client.load(), this.loadKit(), taking?.enabled && this.client.upload ? this.loadFiles() : null, (this.ticketsLoad ??= this.ticketsLoader?.())]);
    if (thread?.turns.some(turn => turn.attachments?.length)) await this.loadFiles();
    if (this.panel !== panel) return;
    if (this.filesKit && taking?.enabled && this.client.upload) {
      this.filesKit.attachField(
        {
          attachLabel: this.strings.attachLabel,
          fileRefusal: this.strings.fileRefusal,
          hold: file => {
            this.picked = file;
          },
          objectUrl: blob => this.objectUrl(blob),
          removeFileLabel: this.strings.removeFileLabel,
          root: this.root,
        },
        composer,
        input,
        log,
        taking,
      );
    }
    const help = this.helpHost();
    if (help?.placement === 'tab' && help.host.source.collections && help.host.source.collectionArticles) help.kit.helpTab(help.host);
    if (help?.placement === 'while_typing') help.kit.whileTyping(help.host, input, composer);
    if (tickets && this.appearance.tickets) tickets.ticketsTab(panel, this.client, this.appearance.tickets.words);
    if (thread && thread.turns.length > 0) {
      this.renderThread(log, thread);
    } else {
      this.renderFirstRun(log, input);
    }
    /* Closed is worth saying even when the project wrote no message of its own.
       Gating this on a non-empty awayMessage left a visitor outside the hours
       looking at a widget that promised a reply in a few minutes and said
       nothing about being shut, which is what clepit's landing showed at 03:14
       UTC against hours of 09:00 to 17:00. */
    if (!this.openNow) {
      this.renderAway(header);
    }
  }

  /// What a visitor meets before they have said anything. Without it the panel
  /// opens onto an empty log, which reads as a chat that failed to load rather
  /// than as one waiting for a first message.
  ///
  /// Rendered into the log rather than beside it, so the first sent turn simply
  /// replaces it and nothing has to be torn down in two places.
  private renderFirstRun(log: HTMLElement, input: HTMLInputElement): void {
    const empty = document.createElement('div');
    empty.className = 'empty';

    const heading = document.createElement('h3');
    heading.textContent = this.appearance.greeting.trim() || this.strings.emptyTitle;
    empty.append(heading);
    const help = this.helpHost();
    if (help?.placement === 'first_view') {
      help.kit.firstView(help.host, empty);
    } else {
      const body = document.createElement('p');
      body.textContent = this.strings.emptyBody;
      empty.append(body);
    }

    /* Buttons, not the preview's spans. A chip that offers an opening line and
       cannot be pressed is decoration, and one that can be pressed has to be
       reachable by keyboard and 44px tall to be reachable by thumb. */
    const entries: readonly unknown[] = this.appearance.starterPrompts;
    entries.filter(isStarterPrompt).forEach((prompt, index) => {
      const control = document.createElement('div');
      control.className = 'prompt';
      const words = document.createElement('div');
      words.className = 'promptwords';
      words.id = `prompt-${index}`;
      const markup = document.createElement('template');
      markup.innerHTML = prompt.html;
      if (isDrawable(markup.content)) words.append(markup.content);
      else words.textContent = prompt.text;
      for (const link of words.querySelectorAll('a')) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      const press = document.createElement('button');
      press.className = 'promptpress';
      press.type = 'button';
      press.setAttribute('aria-labelledby', words.id);
      press.addEventListener('click', () => {
        input.value = prompt.text;
        void this.submit(input, log);
      });
      control.append(words, press);
      empty.append(control);
    });

    log.replaceChildren(empty);
  }

  /// The help centre, when the project searches one, whatever feeds the panel
  /// can read it and its script has loaded. The console's preview passes no
  /// reads, so it draws the panel as it always has.
  private helpHost(): { host: HelpHost; kit: HelpKit; placement: MessengerHelp['placement'] } | null {
    const help = this.appearance.help;
    const { kit, panel } = this;
    const host = help && kit && panel ? kit.hostOf(this.client, { close: () => this.close(), closeLabel: this.strings.closeLabel, panel, scheme: this.scheme, words: help.words }) : null;
    return host && kit && help ? { host, kit, placement: help.placement } : null;
  }

  private renderAway(header: HTMLElement): void {
    const away = document.createElement('div');
    away.className = 'away';
    const glyph = document.createElement('span');
    glyph.className = 'awayglyph';
    glyph.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.textContent = this.appearance.awayMessage.trim() || this.strings.awayBody;
    away.append(glyph, text);
    header.after(away);
  }

  private objectUrl(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.push(url);
    return url;
  }

  close(): void {
    if (!this.panel) return;
    this.panel.remove();
    this.scrim?.remove();
    this.panel = null;
    this.scrim = null;
    this.picked = null;
    for (const url of this.objectUrls) URL.revokeObjectURL(url);
    this.objectUrls = [];
    if (this.lastFocused instanceof HTMLElement) {
      this.lastFocused.focus();
    } else {
      this.launcher.focus();
    }
  }

  /// A turn arriving from realtime while the panel is shut bumps the unread
  /// count; while it is open it appends live.
  onIncomingTurn(turn: ChatTurn): void {
    if (this.panel && turn.attachments?.length && !this.filesKit && this.filesLoader) {
      void this.loadFiles().then(() => this.takeTurn(turn));
      return;
    }
    this.takeTurn(turn);
  }

  private takeTurn(turn: ChatTurn): void {
    if (this.panel) {
      const log = this.panel.querySelector('.log');
      /* The visitor's own turn comes back over the socket as well, and by then
         its bubble has adopted this id. Appending it again drew the message
         twice. */
      if (log instanceof HTMLElement && !this.hasTurn(log, turn.id)) this.appendTurn(log, turn);
      return;
    }
    if (turn.authorKind !== 'end_user') {
      this.unread += 1;
      this.renderBadge();
    }
  }

  private async submit(input: HTMLInputElement, log: HTMLElement): Promise<void> {
    const body = input.value.trim();
    const file = this.picked;
    if (!body && !file) return;
    input.value = '';
    this.picked = null;
    for (const stale of this.root.querySelectorAll('.file,.ferr,.hsugg')) stale.remove();
    await this.sendBody(log, body, file);
  }

  /**
   * Draw the visitor's words, then send them.
   *
   * This used to await the round trip and render the ANSWER, which meant the
   * log stayed empty for the whole of it. On the first message of a thread that
   * is two sequential requests, `POST /v1/conversations` then `POST …/turns`,
   * and a visitor reported roughly three seconds of pressing send at a panel
   * that showed nothing. Nothing about the transport got faster here; what
   * changed is that the widget no longer makes its own reader wait on it to say
   * what they already typed.
   *
   * Separated from `submit` because a retry re-enters here with the same body
   * and no composer to read it from.
   */
  private async sendBody(log: HTMLElement, body: string, file: File | null = null): Promise<void> {
    /* The first-run panel goes as soon as there is a first message, and it goes
       BEFORE the request rather than after: a slow send would otherwise leave
       the visitor looking at the prompt they just pressed. */
    log.querySelector('.empty')?.remove();
    this.pendingTurns += 1;
    const bubble = this.appendTurn(log, { authorId: null, authorKind: 'end_user', body, createdAt: new Date().toISOString(), id: `pending-${this.pendingTurns}` }, file);
    bubble.setAttribute('data-pending', '');

    const uploaded = file ? await this.client.upload?.(file) : undefined;
    const turn = uploaded === null ? null : await this.client.send(body, pageContextNow(), uploaded ? [uploaded.id] : undefined);
    bubble.removeAttribute('data-pending');
    if (turn) {
      /* Adopt the server's id. Until it does, the socket echo of this very turn
         has nothing to match on and appends a duplicate. */
      bubble.dataset.turnId = turn.id;
      return;
    }
    this.markFailed(log, bubble, body, file);
  }

  /**
   * A send that did not land stays on screen, with a way back.
   *
   * The old path cleared the composer and rendered on truth only, so a null
   * answer took the visitor's words with it and said nothing: they had typed a
   * message, watched it vanish, and had no reason to think it had not been
   * sent. Losing someone's message silently is worse than being slow.
   */
  private markFailed(log: HTMLElement, bubble: HTMLElement, body: string, file: File | null): void {
    bubble.setAttribute('data-failed', '');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'retry';
    retry.textContent = this.strings.retryLabel;
    retry.addEventListener('click', () => {
      bubble.remove();
      void this.sendBody(log, body, file);
    });
    (bubble.querySelector('.body') ?? bubble).append(retry);
  }

  /// Whether this log already shows the turn, by the id the element carries.
  private hasTurn(log: HTMLElement, id: string): boolean {
    for (const child of log.children) {
      if (child instanceof HTMLElement && child.dataset.turnId === id) return true;
    }
    return false;
  }

  private renderThread(log: HTMLElement, thread: ChatThread): void {
    log.replaceChildren();
    for (const turn of thread.turns) this.appendTurn(log, turn);
  }

  private appendTurn(log: HTMLElement, turn: ChatTurn, file: File | null = null): HTMLElement {
    /* Covers the agent who speaks first: a turn arriving from the poll while the
       panel is open and still showing its first-run state would otherwise land
       underneath the starter prompts. */
    log.querySelector('.empty')?.remove();
    if (turn.authorKind === 'system') {
      const notice = document.createElement('div');
      notice.className = 'turn system';
      notice.dataset.turnId = turn.id;
      notice.textContent = turn.body;
      log.append(notice);
      return notice;
    }
    const kind = AUTHOR_KINDS.includes(turn.authorKind) ? turn.authorKind : 'agent';
    const settings = this.settings;
    const bubble = document.createElement('div');
    bubble.className = `turn ${kind}`;
    /* The turn's identity on the element, so a turn already drawn can be
       recognised. Two callers need that: an optimistic bubble adopting the id
       the server gives it, and the socket echo of the visitor's own turn, which
       must find the message it already is rather than land beside it. */
    bubble.dataset.turnId = turn.id;

    // An avatar is a sibling of the body, not a child of it, or the face sits
    // inside the fill it is meant to stand beside.
    const wantsAvatar = kind !== 'end_user' && settings.nameAvatar;
    if (wantsAvatar) {
      const avatar = document.createElement('span');
      avatar.className = kind === 'ai' ? 'av bot' : 'av';
      avatar.setAttribute('aria-hidden', 'true');
      if (kind === 'agent' && turn.authorAvatarUrl) {
        const picture = document.createElement('img');
        picture.alt = '';
        picture.src = this.client.avatarUrl(turn.authorAvatarUrl);
        avatar.append(picture);
      } else {
        avatar.textContent = kind === 'ai' ? this.strings.aiLabel : initialsOf(this.agentName(turn));
        const tone = kind === 'agent' ? this.appearance.team?.find(face => face.name === turn.authorName)?.tone : undefined;
        if (tone) avatar.style.background = tone;
      }
      bubble.append(avatar);
    }

    const body = document.createElement('div');
    body.className = 'body';
    if (kind === 'end_user') {
      const kit = this.filesKit;
      const url = (blob: Blob): string => this.objectUrl(blob);
      if (file) kit?.drawFile(body, file.name, file.type, file.size, Promise.resolve(file), url);
      for (const attachment of turn.attachments ?? []) {
        kit?.drawFile(body, attachment.name, attachment.contentType, attachment.bytes, this.client.file?.(attachment.url) ?? Promise.resolve(null), url);
      }
    }

    // §6, and the design record's own constraint: an AI reply carries a TEXT
    // label in every name placement, including hidden, whether or not it also
    // carries an avatar. A machine told apart by an avatar shape alone is not
    // told apart.
    const named = kind !== 'end_user' && settings.namePlacement !== 'hidden';
    const text = document.createElement('div');
    if (kind === 'ai') linkify(text, turn.body);
    else text.textContent = turn.body;
    if (named || (kind === 'ai' && settings.namePlacement === 'hidden')) {
      const author = document.createElement('div');
      author.className = kind === 'ai' ? 'author ai' : 'author';
      author.textContent = kind === 'ai' ? this.strings.aiLabel : this.agentName(turn);
      if (settings.stampPlacement === 'beside_name') {
        author.append(this.stamp(turn));
      }
      /* An inline name is the row UNDER the words (ruling 5 of 2026-09-13): the
         words are what the reader came for, and the name is who said them. */
      if (settings.namePlacement === 'inline') {
        author.classList.add('below');
        body.append(text, author);
      } else {
        body.append(author, text);
      }
    } else if (turn.body) {
      body.append(text);
    }
    if (settings.stampPlacement === 'under_turn') {
      body.append(this.stamp(turn));
    }
    bubble.append(body);
    log.append(bubble);
    log.scrollTop = log.scrollHeight;
    return bubble;
  }

  /// The author's name as the messenger shows it (user picks of 2026-09-13),
  /// or the generic label for an author the project does not show.
  private agentName(turn: ChatTurn): string {
    return turn.authorName || this.strings.agentLabel;
  }

  /// The clock as its own element so a stylesheet can place it, rather than a
  /// string concatenated into the author line where CSS cannot reach it.
  private stamp(turn: ChatTurn): HTMLElement {
    const stamp = document.createElement('span');
    stamp.className = 'stamp';
    stamp.textContent = this.strings.timeLabel(turn.createdAt);
    return stamp;
  }

  private onKeydown(event: KeyboardEvent, panel: HTMLElement, close: HTMLElement): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    // Trap focus inside the dialog: tabbing past the last control wraps to the
    // first, and shift-tabbing past the first wraps to the last.
    const focusable = [...panel.querySelectorAll<HTMLElement>('button, input, a[href], [tabindex]:not([tabindex="-1"])')].filter(control => !control.closest('[hidden]') && !control.hidden);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.root.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (active === null) {
      event.preventDefault();
      close.focus();
    }
  }

  private renderBadge(): void {
    if (this.unread > 0) {
      this.badge.hidden = false;
      this.badge.textContent = String(this.unread);
      this.launcher.setAttribute('aria-label', `${this.strings.launcherLabel}, ${this.strings.unreadLabel(this.unread)}`);
    } else {
      this.badge.hidden = true;
      this.badge.textContent = '';
      this.launcher.setAttribute('aria-label', this.strings.launcherLabel);
    }
  }
}

/// On a phone the panel is the codebase's bottom sheet, and a sheet comes down
/// the way the shared one does: past 72px or 40% of its height, or a flick.
/// A drag never starts on a control or inside content the visitor has scrolled.
const SHEET = '(max-width: 770px)';

function dragToClose(panel: HTMLElement, close: () => void): void {
  let drag: { id: number; startY: number; at: number; offset: number } | null = null;
  panel.addEventListener('pointerdown', event => {
    if (drag || event.button !== 0 || !globalThis.matchMedia?.(SHEET).matches) return;
    let node = event.target as Element | null;
    if (node?.closest('a, button, input, textarea, iframe')) return;
    for (; node && node !== panel; node = node.parentElement) if (node.scrollTop > 0) return;
    drag = { at: event.timeStamp, id: event.pointerId, offset: 0, startY: event.clientY };
    panel.setPointerCapture?.(event.pointerId);
    panel.dataset.dragging = '';
  });
  panel.addEventListener('pointermove', event => {
    if (drag?.id !== event.pointerId) return;
    const raw = event.clientY - drag.startY;
    drag.offset = raw >= 0 ? raw : -16 * (1 - Math.exp(raw / 48));
    panel.style.translate = `0 ${drag.offset}px`;
  });
  const release = (event: PointerEvent): void => {
    const held = drag;
    if (held?.id !== event.pointerId) return;
    drag = null;
    delete panel.dataset.dragging;
    const far = held.offset > Math.min(72, panel.getBoundingClientRect().height * 0.4);
    const fast = held.offset / Math.max(1, event.timeStamp - held.at) > 0.5 && held.offset > 16;
    if (far || fast) close();
    else panel.style.translate = '';
  };
  panel.addEventListener('pointerup', release);
  panel.addEventListener('pointercancel', release);
}

const styleShadowRoot = (root: ShadowRoot): ((css: string) => void) => {
  if (typeof CSSStyleSheet === 'function' && 'adoptedStyleSheets' in root && 'replaceSync' in CSSStyleSheet.prototype) {
    const sheet = new CSSStyleSheet();
    root.adoptedStyleSheets = [sheet];
    return css => sheet.replaceSync(css);
  }
  const element = document.createElement('style');
  root.append(element);
  return css => {
    element.textContent = css;
  };
};
